import dayjs from 'dayjs'

import { Article } from '@core/app/types/globalApiType'

export type ArticlesByDate = Record<string, Article[]>

const toDateKey = (created: string): string => {
    const datePart = created?.split(' ')[0] ?? ''
    const match = datePart.match(/^(\d{4})-(\d{2})-(\d{2})$/)
    if (!match) return ''

    const [, year, month, day] = match
    return `${day}-${month}-${year}`
}

export const groupArticlesByDate = (articles: Article[] = []): ArticlesByDate =>
    articles.reduce<ArticlesByDate>((grouped, article) => {
        const dateKey = toDateKey(article.Created)
        if (!dateKey) return grouped

        grouped[dateKey] = grouped[dateKey] ?? []
        grouped[dateKey].push(article)

        return grouped
    }, {})

const parseDateKey = (dateKey: string) => {
    const [day, month, year] = dateKey.split('-')
    return dayjs(`${year}-${month}-${day}`)
}

export const getMonthKey = (dateKey: string): string => dateKey.slice(3)

const FULL_MONTH_KEY_START = 477

const shortMonthName = (date: dayjs.Dayjs, t: (key: string) => string): string => {
    const key = String(FULL_MONTH_KEY_START + date.month())
    const fullName = t(key)?.trim()

    if (fullName && fullName !== key) return fullName.slice(0, 3)

    return date.format('MMM')
}

export const formatMonthLabel = (dateKey: string, t: (key: string) => string): string => {
    const parsed = parseDateKey(dateKey)
    if (!parsed.isValid()) return dateKey

    return `${shortMonthName(parsed, t)} ${parsed.format('YYYY')}`
}

export const formatArticleDate = (created: string, t: (key: string) => string): string => {
    const datePart = created?.split(' ')[0]
    const parsed = dayjs(datePart)
    if (!parsed.isValid()) return ''

    return `${shortMonthName(parsed, t)} ${parsed.format('D')}`
}

export const formatArticleCreatedDate = (created: string, t: (key: string) => string): string => {
    const parsed = dayjs(created)
    if (!parsed.isValid()) return ''

    return `${parsed.format('D')} ${shortMonthName(parsed, t)} ${parsed.format('YYYY')}`
}

export const toPlainText = (value: string): string => value.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim()
